'use client';

import * as React from 'react';
import { Label } from '@/components/ui/label';
import {
  Toggle as TogglePrimitive,
  type ToggleProps as TogglePrimitiveProps,
  toggleVariants,
} from '@/components/ui/toggle';
import {
  ToggleGroupItem as ToggleGroupItemPrimitive,
  type ToggleGroupItemProps as ToggleGroupItemPrimitiveProps,
  ToggleGroup as ToggleGroupPrimitive,
  type ToggleGroupProps as ToggleGroupPrimitiveProps,
} from '@/components/ui/toggle-group';
import { useControllableState } from '@/hooks/use-controllable-state';

// ============================================
// Toggle (with controlled/uncontrolled support)
// ============================================

export interface ToggleProps
  extends Omit<
    TogglePrimitiveProps,
    'defaultPressed' | 'onPressedChange' | 'pressed' | 'size' | 'variant'
  > {
  /** Visual appearance of the toggle */
  variant?: TogglePrimitiveProps['variant'];
  /** Height and horizontal padding of the toggle */
  size?: TogglePrimitiveProps['size'];
  label?: string;
  /** Controlled pressed state. When provided, component is controlled. */
  pressed?: boolean;
  /** Initial pressed state for uncontrolled mode */
  defaultPressed?: boolean;
  /** Callback when pressed state changes */
  onPressedChange?: (pressed: boolean) => void;
  /** Required aria-label for icon-only toggles (when no text children) */
  'aria-label'?: string;
}

function Toggle({
  label,
  className,
  variant = 'default',
  size = 'default',
  pressed: controlledPressed,
  defaultPressed = false,
  onPressedChange,
  ...props
}: ToggleProps) {
  const id = React.useId();

  const [pressed, handlePressedChange] = useControllableState({
    value: controlledPressed,
    defaultValue: defaultPressed,
    onChange: onPressedChange,
  });

  if (label) {
    return (
      <div
        className="flex items-center gap-2"
        data-disabled={props.disabled || undefined}
      >
        <TogglePrimitive
          id={id}
          data-slot="toggle"
          className={className}
          variant={variant}
          size={size}
          pressed={pressed}
          onPressedChange={handlePressedChange}
          {...props}
        />
        <Label htmlFor={id} className="cursor-pointer">
          {label}
        </Label>
      </div>
    );
  }

  return (
    <TogglePrimitive
      data-slot="toggle"
      className={className}
      variant={variant}
      size={size}
      pressed={pressed}
      onPressedChange={handlePressedChange}
      {...props}
    />
  );
}

Toggle.displayName = 'Toggle';

// ============================================
// ToggleGroup (with controlled/uncontrolled support)
// ============================================

export interface ToggleGroupProps
  extends Omit<
    ToggleGroupPrimitiveProps,
    'defaultValue' | 'onValueChange' | 'value'
  > {
  label?: string;
  /** Spacing between items (0 for connected group) */
  spacing?: number;
  /** Orientation of the group */
  orientation?: 'horizontal' | 'vertical';
  /** Shape of the connected group corners */
  shape?: 'rounded' | 'round';
  /** Controlled value. When provided, component is controlled. */
  value?: readonly string[];
  /** Initial selected value(s) for uncontrolled mode */
  defaultValue?: readonly string[];
  /** Callback when value changes */
  onValueChange?: (value: readonly string[]) => void;
}

function ToggleGroup({
  label,
  className,
  children,
  variant,
  size,
  spacing = 0,
  orientation = 'horizontal',
  shape = 'rounded',
  value: controlledValue,
  defaultValue,
  onValueChange,
  ...props
}: ToggleGroupProps) {
  const [value, handleValueChange] = useControllableState({
    value: controlledValue,
    defaultValue: defaultValue ?? [],
    onChange: onValueChange,
  });

  const groupContent = (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      variant={variant}
      size={size}
      spacing={spacing}
      orientation={orientation}
      shape={shape}
      className={className}
      value={value}
      onValueChange={handleValueChange}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive>
  );

  if (label) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        {groupContent}
      </div>
    );
  }

  return groupContent;
}

ToggleGroup.displayName = 'ToggleGroup';

// ============================================
// ToggleGroupItem
// ============================================

export interface ToggleGroupItemProps extends ToggleGroupItemPrimitiveProps {}

function ToggleGroupItem({
  className,
  children,
  variant = 'default',
  size = 'default',
  ...props
}: ToggleGroupItemProps) {
  return (
    <ToggleGroupItemPrimitive
      data-slot="toggle-group-item"
      className={className}
      variant={variant}
      size={size}
      {...props}
    >
      {children}
    </ToggleGroupItemPrimitive>
  );
}

ToggleGroupItem.displayName = 'ToggleGroupItem';

export { Toggle, ToggleGroup, ToggleGroupItem, toggleVariants };
